// API Configuration
// This file centralizes all API endpoints and makes them configurable

import { currentDeployment } from './deployment';

interface ApiConfig {
  baseUrl: string;
  wsUrl: string;
  endpoints: {
    textChat: string;
    conversationHistory: string;
    conversationSessions: string;
    conversationMessages: string;
    users: string;
    stats: string;
  };
}

// Get configuration from deployment config
const getApiConfig = (): ApiConfig => {
  const baseUrl = currentDeployment.apiBaseUrl;
  const wsUrl = currentDeployment.wsUrl;

  return {
    baseUrl,
    wsUrl,
    endpoints: {
      textChat: `${baseUrl}/api/text-chat`,
      conversationHistory: `${baseUrl}/api/conversations/history`,
      conversationSessions: `${baseUrl}/api/conversations/sessions`,
      conversationMessages: `${baseUrl}/api/conversations/messages`,
      users: `${baseUrl}/api/users`,
      stats: `${baseUrl}/api/stats`
    }
  };
};

// Export the configuration
export const apiConfig = getApiConfig();

// Export individual endpoints for convenience
export const {
  baseUrl,
  wsUrl,
  endpoints: {
    textChat,
    conversationHistory,
    conversationSessions,
    conversationMessages,
    users,
    stats
  }
} = apiConfig;

/** Local aiohttp is almost always plain HTTP; WSS to localhost:8000 will fail TLS. */
function normalizeWebSocketBaseUrl(raw: string): string {
  if (!raw) return raw;
  try {
    const u = new URL(raw);
    const local = u.hostname === 'localhost' || u.hostname === '127.0.0.1';
    if (local && u.protocol === 'wss:') {
      u.protocol = 'ws:';
      return u.origin;
    }
  } catch {
    /* ignore */
  }
  return raw.replace(/\/$/, '');
}

// Export a function to get the WebSocket URL with optional user id and SSO email (for conversation logging).
export const getWebSocketUrl = (userId?: string, userEmail?: string): string => {
  const base = normalizeWebSocketBaseUrl(wsUrl);
  const url = new URL('/realtime', base);
  if (userId) {
    url.searchParams.set('user_id', userId);
  }
  if (userEmail) {
    url.searchParams.set('user_email', userEmail);
  }
  return url.toString();
};

// Export a function to get the text chat URL
export const getTextChatUrl = (): string => textChat;

/** POST `/api/conversations/messages` — JSON body (no query params). */
export function postConversationMessages(params: {
  user_email: string;
  limit?: number;
  type?: string;
  session_id?: string;
}): Promise<Response> {
  return fetch(conversationMessages, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      user_email: params.user_email,
      limit: params.limit ?? 500,
      ...(params.type != null && params.type !== "" ? { type: params.type } : {}),
      ...(params.session_id ? { session_id: params.session_id } : {}),
    }),
  });
}

/** POST `/api/conversations/history` — JSON body: session_id, user_email, optional limit. */
export function postConversationHistory(params: {
  session_id: string;
  user_email: string;
  limit?: number;
}): Promise<Response> {
  return fetch(conversationHistory, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      session_id: params.session_id,
      user_email: params.user_email,
      limit: params.limit ?? 50,
    }),
  });
}

/** POST `/api/conversations/sessions` — JSON body: user_email, optional limit, optional type. */
export function postConversationSessions(params: {
  user_email: string;
  limit?: number;
  type?: string;
}): Promise<Response> {
  return fetch(conversationSessions, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      user_email: params.user_email,
      limit: params.limit ?? 100,
      ...(params.type != null && params.type !== "" ? { type: params.type } : {}),
    }),
  });
}

// Export a function to get the users URL
export const getUsersUrl = (limit?: number): string => {
  const url = new URL(users);
  if (limit) {
    url.searchParams.set('limit', limit.toString());
  }
  return url.toString();
};

// Export a function to get the stats URL
export const getStatsUrl = (userId?: string): string => {
  const url = new URL(stats);
  if (userId) {
    url.searchParams.set('user_id', userId);
  }
  return url.toString();
};
